const mobile = matchMedia('(max-width: 749px)');
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const states = new Map();
let scheduled = false;

function select(state, index) {
  const slideshow = state.element.querySelector('slideshow-component');
  const image = slideshow?.slides?.[index]?.querySelector('img');
  if (image) {
    image.loading = 'eager';
    if (!image.complete || !image.naturalWidth) return;
  }
  if (typeof slideshow?.select === 'function' && slideshow.current !== index) {
    slideshow.select(index, undefined, { animate: false });
  }
}

function removeOverlay(state) {
  state.overlay?.remove();
  state.overlay = null;
  state.copy = null;
}

function manualControl(state) {
  if (state.manual) return;
  state.manual = true;
  select(state, state.progress >= 0.5 ? 1 : 0);
  removeOverlay(state);
}

function render(state, progress) {
  state.progress = progress;
  if (progress === 0 || progress === 1 || reducedMotion.matches) {
    select(state, progress >= 0.5 ? 1 : 0);
    removeOverlay(state);
    return;
  }
  const slideshow = state.element.querySelector('slideshow-component');
  const image = slideshow?.slides?.[1]?.querySelector('img');
  if (!image) return;
  image.loading = 'eager';
  if (!image.complete || !image.naturalWidth) return;
  if (!state.overlay) {
    const overlay = document.createElement('div');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('data-nfl-scroll-reveal', '');
    overlay.style.cssText = 'position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:2;background:var(--color-background, #fff);';
    const copy = document.createElement('img');
    copy.alt = '';
    copy.style.cssText = 'display:block;width:100%;height:100%;margin:0;';
    const style = getComputedStyle(image);
    copy.style.objectFit = style.objectFit;
    copy.style.objectPosition = style.objectPosition;
    overlay.append(copy);
    state.overlay = overlay;
    state.copy = copy;
    state.element.append(overlay);
  }
  const source = image.currentSrc || image.src;
  if (state.copy.src !== source) state.copy.src = source;
  state.overlay.hidden = !state.copy.complete || !state.copy.naturalWidth;
  state.overlay.style.clipPath = 'inset(' + (1 - progress) * 100 + '% 0 0 0)';
  select(state, 0);
}

function refresh() {
  scheduled = false;
  if (document.hidden) return;
  const active = mobile.matches;
  const viewport = innerHeight;
  const scroll = Math.max(0, window.scrollY);
  for (const state of states.values()) {
    if (!state.element.isConnected) {
      removeOverlay(state);
      observer.unobserve(state.element);
      states.delete(state.element);
      continue;
    }
    if (!active) {
      removeOverlay(state);
      continue;
    }
    const rect = state.element.getBoundingClientRect();
    if (rect.height <= 0) continue;
    const visible = rect.bottom > 0 && rect.top < viewport;
    if (!visible) {
      removeOverlay(state);
      state.manual = false;
      state.pressed = false;
      continue;
    }
    if (state.manual) continue;
    const start = Math.max(0, rect.top + scroll - viewport * 0.72);
    const travel = Math.max(120, Math.min(rect.height, viewport * 0.3));
    const progress = Math.max(0, Math.min(1, (scroll - start) / travel));
    render(state, progress);
  }
}

function schedule() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(refresh);
}

const observer = new IntersectionObserver(entries => {
  if (mobile.matches) {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const image = entry.target.querySelector('slideshow-component')?.slides?.[1]?.querySelector('img');
      if (image) image.loading = 'eager';
    }
  }
  schedule();
}, { rootMargin: '200px 0px', threshold: [0, 0.5, 1] });

function discover() {
  for (const element of document.querySelectorAll('[data-nfl-auto-preview]')) {
    if (states.has(element)) continue;
    const state = { element, progress: 0, manual: false, pressed: false, overlay: null, copy: null };
    states.set(element, state);
    observer.observe(element);
    element.addEventListener('load', schedule, true);
    element.addEventListener('pointerdown', event => {
      state.pressed = true;
      state.x = event.clientX;
      state.y = event.clientY;
    }, { passive: true });
    element.addEventListener('pointermove', event => {
      if (!mobile.matches || !state.pressed) return;
      const x = Math.abs(event.clientX - state.x);
      const y = Math.abs(event.clientY - state.y);
      if (x > 10 && x > y) manualControl(state);
    }, { passive: true, capture: true });
    element.addEventListener('keydown', event => {
      if (mobile.matches && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) manualControl(state);
    }, { capture: true });
  }
  schedule();
}

for (const type of ['pointerup', 'pointercancel']) {
  document.addEventListener(type, () => {
    for (const state of states.values()) state.pressed = false;
    schedule();
  }, { passive: true });
}
document.addEventListener('visibilitychange', schedule);
window.addEventListener('scroll', schedule, { passive: true });
window.addEventListener('resize', schedule, { passive: true });
mobile.addEventListener('change', () => {
  for (const state of states.values()) {
    removeOverlay(state);
    if (!state.manual) select(state, 0);
    state.manual = false;
  }
  schedule();
});
reducedMotion.addEventListener('change', schedule);
await customElements.whenDefined('slideshow-component');
discover();
new MutationObserver(discover).observe(document.body, { childList: true, subtree: true });
